import { log } from '@/Utils/logger';
import Swal, { type SweetAlertOptions } from 'sweetalert2';

const escapeHtml = (text: string) => text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

export const showSuccessPopupDialog = async ({
  text,
  title = 'Success!',
  type = 'text',
  customClass,
  width,
  warningMessage,
}: {
  text?: string;
  title?: string;
  type?: 'text' | 'html';
  customClass?: string;
  width?: string | number;
  warningMessage?: string;
}) => {
  log.info({
    func: 'showSuccessPopupDialog',
    title,
    text,
    warningMessage,
  });

  const warningHtml = warningMessage
    ? `<br><br><strong>Warning:</strong> <span style="white-space: pre-wrap;">${escapeHtml(warningMessage)}</span>`
    : '';
  const html = type === 'html' ? `${text ?? ''}${warningHtml}` : warningHtml || undefined;

  await Swal.fire({
    title,
    text: type === 'text' ? text : '',
    html: type === 'html' ? html : warningHtml || undefined,
    icon: 'success',
    width,
    customClass: {
      popup: `swal2-custom-icon swal2-success-modal ${customClass ?? ''}`,
      confirmButton: 'btn btn-purple btn-fill',
    },
    buttonsStyling: false,
    allowOutsideClick: false,
  });
};

export const showErrorPopupDialog = async ({
  text,
  title = 'Error!',
  type = 'text',
  customClass,
}: {
  text?: string;
  title?: string;
  type?: 'text' | 'html';
  customClass?: string;
}) => {
  log.info({
    func: 'showErrorPopupDialog',
    title,
    text,
  });

  await Swal.fire({
    title,
    text: type === 'text' ? text : '',
    html: type === 'html' ? text : '',
    icon: 'error',
    customClass: {
      popup: `swal2-custom-icon swal2-error-modal ${customClass ?? ''}`,
      confirmButton: 'btn btn-purple btn-fill',
    },
    buttonsStyling: false,
    allowOutsideClick: false,
  });
};

export const showWarningPopupDialog = async ({
  text,
  title = 'Alert!',
  type = 'text',
  icon = 'warning',
  customClass,
  size = 'default',
  confirmButtonText = 'OK',
}: {
  text?: string;
  title?: string;
  type?: 'text' | 'html';
  icon?: 'question' | 'info' | 'warning';
  customClass?: string;
  size?: 'small' | 'medium' | 'default' | 'large' | 'full';
  confirmButtonText?: string;
}) => {
  log.info({
    func: 'showWarningPopupDialog',
    title,
    text,
  });

  await Swal.fire({
    title,
    text: type === 'text' ? text : '',
    html: type === 'html' ? text : '',
    icon,
    customClass: {
      popup: `swal2-custom-icon ${customClass ?? ''} size-${size}`,
      confirmButton: 'btn btn-fill ' + (icon === 'question' ? 'btn-purple' : 'btn-warning'),
    },
    confirmButtonText,
    buttonsStyling: false,
    allowOutsideClick: false,
  });
};

export const showConfirmPopupDialog = async ({
  text,
  title,
  confirmButtonText = 'Yes',
  cancelButtonText = 'No',
  type = 'text',
  icon = 'question',
  customClass,
  denyButtonText,
  confirmButtonDisabled = false,
}: {
  title?: string;
  text?: string;
  confirmButtonText?: string;
  cancelButtonText?: string;
  type?: 'text' | 'html';
  icon?: 'question' | 'warning' | 'info' | 'error';
  customClass?: string;
  denyButtonText?: string;
  confirmButtonDisabled?: boolean;
}) => {
  log.info({
    func: 'showConfirmPopupDialog',
    title,
    text,
  });

  const response = await Swal.fire({
    title,
    text: type === 'text' ? text : '',
    html: type === 'html' ? text : '',
    icon,
    customClass: {
      popup: `swal2-custom-icon ${customClass ?? ''}`,
      confirmButton: 'btn btn-purple btn-fill',
      cancelButton: 'btn btn-yellow btn-fill',
      denyButton: 'btn btn-blue btn-fill',
    },
    showCancelButton: true,
    showDenyButton: !!denyButtonText,
    confirmButtonText,
    cancelButtonText,
    denyButtonText,
    buttonsStyling: false,
    allowOutsideClick: false,
    didOpen: () => {
      const confirmButton = Swal.getConfirmButton();

      if (confirmButton) {
        confirmButton.disabled = confirmButtonDisabled;
      }
    },
  });

  /*
      If confirm dialog has deny button, the response has 3 status like isConfirmed, isDenied and isDismissed
      But response.value is only true for isConfirmed status and we can't get deny status.
      So if confirm popup has deny button, return json so that we can set difference action for each status.
    */
  return denyButtonText ? response : response.value;
};

export const showConfirmPopupDialogWithValidation = async ({
  text,
  title,
  type = 'text',
  inputValue,
  input = 'text',
  inputPlaceholder = 'Please input the text',
  validationText = 'Please input the text',
  inputOptions,
  confirmButtonText = 'Ok',
  cancelButtonText = 'No',
  dateValidation: { minDate = '', maxDate = '' } = {},
  preConfirm,
}: {
  text?: string;
  title?: string;
  type?: 'text' | 'html';
  input: 'text' | 'textarea' | 'number' | 'checkbox' | 'radio' | 'select' | 'date';
  inputPlaceholder?: string;
  inputValue?: SweetAlertOptions['inputValue'];
  validationText?: string;
  inputOptions?: { [key: string]: string };
  preConfirm?: (value: any) => any;
  confirmButtonText?: string;
  cancelButtonText?: string;
  dateValidation?: {
    minDate?: string;
    maxDate?: string;
  };
}) => {
  log.info({
    func: 'showConfirmPopupDialogWithValidation',
    title,
    text,
  });

  const btnResponse = await Swal.fire({
    icon: 'question',
    title,
    text: type === 'text' ? text : '',
    html: type === 'html' ? text : '',
    input,
    inputValue,
    inputOptions,
    inputPlaceholder,
    preConfirm,
    inputValidator: (value: string) => {
      // if validationText is not exist, it means no need to validate the value
      return value || !validationText ? null : validationText;
    },
    customClass: {
      popup: 'swal2-custom-icon',
      confirmButton: 'btn btn-purple btn-fill',
      cancelButton: 'btn btn-yellow btn-fill',
    },
    showCancelButton: true,
    confirmButtonText,
    cancelButtonText,
    buttonsStyling: false,
    allowOutsideClick: false,
    didOpen: () => {
      if (input !== 'date') {
        return;
      }
      const inputElement = Swal.getInput();

      if (!inputElement) {
        return;
      }

      if (minDate) {
        inputElement.min = minDate;
      }

      if (maxDate) {
        inputElement.max = maxDate;
      }
    },
  });

  return btnResponse.value;
};

// it all works without wrapping, keep it only for backwards-compatibility with existing code
export const useSwalDialog = () => {
  return {
    showSuccessPopupDialog,
    showErrorPopupDialog,
    showWarningPopupDialog,
    showConfirmPopupDialog,
    showConfirmPopupDialogWithValidation,
  };
};
